/**
 * Карта этапов проекта: где проект сейчас, что даёт каждый этап и в каком
 * порядке идут задачи.
 *
 * Flow: App грузит состояние проекта, этапы и карточки → карта показывает
 *       состояние сверху, ниже карточку на каждый этап → перетаскивание строки
 *       вызывает перестановку в очереди → App перезагружает данные.
 *
 * Классы намеренно в своём пространстве имён (ms-*): имя .stage в стилях уже
 * занято этапами КАРТОЧКИ, и раздел этапа ПРОЕКТА наследовал от него
 * горизонтальную раскладку — абзацы вставали колонками.
 *
 * Зависимости:
 *   - react: состояние перетаскивания и редактирования состояния проекта;
 *   - ./model: цвета приоритетов и подписи статусов.
 *
 * Связанные компоненты:
 *   - App.jsx — грузит данные, переключает карту и доску;
 *   - Board.jsx — те же карточки в виде доски по статусам.
 */
import { useState } from 'react'
import { COLUMNS, priorityColor } from './model.js'

const statusTitle = (key) => COLUMNS.find((c) => c.key === key)?.title || key

const dateText = (iso) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit' })
}

/** Общее состояние проекта: текст, который агент обновляет после каждой задачи. */
function ProjectStatus({ status, onSave, readOnly }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  if (!status) return null

  if (editing) {
    // Предел приходит из ответа сервиса, а не хранится здесь числом: вторая
    // копия разошлась бы с оригиналом при первом же изменении, и молча.
    const limit = status.limit
    // Руны, а не единицы кода: русская буква занимает один знак, и счётчик
    // обязан показывать то же число, что проверяет сервис.
    const used = [...draft.trim()].length
    return (
      <section className="ms-status ms-status-edit">
        <textarea rows={5} maxLength={limit} value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Где проект сейчас · что в работе · что дальше" />
        <div className="ms-row">
          <button className="btn" disabled={used === 0}
                  onClick={async () => { await onSave(draft); setEditing(false) }}>
            Сохранить
          </button>
          <button className="btn ghost" onClick={() => setEditing(false)}>Отмена</button>
          <span className="ms-meta">
            {used} из {limit} знаков. Перечня закрытых карточек здесь нет —
            его показывает карта этапов ниже.
          </span>
        </div>
      </section>
    )
  }

  return (
    <section className={`ms-status${status.stale ? ' ms-stale' : ''}`}>
      <div className="ms-status-head">
        <h2>Состояние проекта</h2>
        <span className="ms-meta">
          обновлено {dateText(status.updated_at)}
          {status.updated_by ? ` · ${status.updated_by}` : ''}
        </span>
        {status.stale && (
          <span className="badge warn" title="После обновления текста закрывались задачи">
            устарело: закрыто задач с тех пор — {status.done_since_update}
          </span>
        )}
        {!readOnly && (
          <button className="btn ghost" onClick={() => { setDraft(status.body || ''); setEditing(true) }}>
            Изменить
          </button>
        )}
      </div>
      {status.body
        ? <p className="ms-status-body">{status.body}</p>
        : <p className="ms-muted">Состояние ещё не описано.</p>}
    </section>
  )
}

/** Одна строка очереди внутри этапа. */
function Row({ item, position, isNext, onOpen, onDragStart, onDrop, readOnly }) {
  const [over, setOver] = useState(false)
  return (
    <tr
      className={`${isNext ? 'ms-next ' : ''}${over ? 'ms-over' : ''}`}
      draggable={!readOnly}
      onDragStart={(e) => onDragStart(e, item)}
      onDragOver={(e) => { if (!readOnly) { e.preventDefault(); setOver(true) } }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDrop(item) }}
    >
      <td className="ms-pos">{position}</td>
      <td className="ms-name">
        <button type="button" className="ms-link" onClick={() => onOpen(item.id)}>{item.title}</button>
        {isNext && <span className="badge next">следующая</span>}
      </td>
      <td className="ms-prio">
        <span className="badge" style={{ borderColor: priorityColor(item.priority) }}>{item.priority}</span>
      </td>
      <td className="ms-status-cell">{statusTitle(item.status)}</td>
    </tr>
  )
}

/** Карточка одного этапа: заголовок, прогресс, абзац описания, задачи. */
function Milestone({ m, items, numberOf, onOpen, onDragStart, onDrop, readOnly }) {
  const [openCriteria, setOpenCriteria] = useState(false)
  const p = m.progress || {}
  const stageItems = (m.item_ids || []).map((id) => items[id]).filter(Boolean)

  // Описание одним абзацем: цель, состав и способ проверки читаются связным
  // текстом. Четыре подписанных блока давали больше пустоты, чем смысла.
  const paragraph = [m.goal, m.deliverables, m.verification].filter(Boolean).join(' ')

  return (
    <section className="ms-card">
      <div className="ms-head">
        <h2>{m.title || m.key}</h2>
        <div className="ms-bar"><div className="ms-fill" style={{ width: `${p.percent || 0}%` }} /></div>
        <span className="ms-count">
          {p.done || 0}/{p.total || 0}
          {p.in_progress ? ` · в работе ${p.in_progress}` : ''}
          {p.blocked ? ` · блок ${p.blocked}` : ''}
        </span>
      </div>

      {paragraph && <p className="ms-text">{paragraph}</p>}

      {m.readiness_criteria?.length > 0 && (
        <div className="ms-criteria">
          <button type="button" className="ms-link ms-toggle" onClick={() => setOpenCriteria(!openCriteria)}>
            {openCriteria ? '▾' : '▸'} критерии готовности ({m.readiness_criteria.length})
          </button>
          {openCriteria && (
            <ul>{m.readiness_criteria.map((c, n) => <li key={n}>{c}</li>)}</ul>
          )}
        </div>
      )}

      {stageItems.length === 0
        ? <p className="ms-muted">Задач нет.</p>
        : (
          <table className="ms-table">
            <tbody>
              {stageItems.map((i) => (
                <Row key={i.id} item={i} position={numberOf[i.id] ?? '—'}
                     isNext={i.id === p.next_item_id} onOpen={onOpen}
                     onDragStart={onDragStart} onDrop={onDrop} readOnly={readOnly} />
              ))}
            </tbody>
          </table>
        )}
    </section>
  )
}

export default function StageMap({ milestones, items, status, onOpen, onReorder, onSaveStatus, readOnly }) {
  const [dragged, setDragged] = useState(null)
  const byId = Object.fromEntries(items.map((i) => [i.id, i]))

  // Сквозная нумерация: очередь одна на весь проект, карточки «Общие» стоят
  // в ней вперемежку с продуктовыми.
  const ordered = items
    .filter((i) => i.queue_position !== null && i.queue_position !== undefined && !i.archived_at)
    .sort((a, b) => a.queue_position - b.queue_position)
  const numberOf = Object.fromEntries(ordered.map((i, n) => [i.id, n + 1]))
  const unassigned = items.filter((i) => !i.milestone && !i.archived_at)

  function handleDragStart(event, item) {
    setDragged(item)
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', item.id)
  }

  function handleDrop(target) {
    if (!dragged || dragged.id === target.id) return
    const idx = ordered.findIndex((i) => i.id === target.id)
    const prev = idx > 0 ? ordered[idx - 1] : null
    onReorder(dragged.id, prev ? prev.id : null, target.id)
    setDragged(null)
  }

  return (
    <div className="ms-map">
      <ProjectStatus status={status} onSave={onSaveStatus} readOnly={readOnly} />

      {milestones.map((m) => (
        <Milestone key={m.key} m={m} items={byId} numberOf={numberOf} onOpen={onOpen}
                   onDragStart={handleDragStart} onDrop={handleDrop} readOnly={readOnly} />
      ))}

      {unassigned.length > 0 && (
        <section className="ms-card">
          <div className="ms-head"><h2>Вне этапов</h2><span className="ms-count">{unassigned.length}</span></div>
          <p className="ms-text">
            Задачи без этапа. В прогресс этапов они не входят — этап не должен выглядеть
            готовым из-за того, что задачу к нему не привязали.
          </p>
          <table className="ms-table">
            <tbody>
              {unassigned.map((i) => (
                <Row key={i.id} item={i} position={numberOf[i.id] ?? '—'} isNext={false}
                     onOpen={onOpen} onDragStart={handleDragStart} onDrop={handleDrop}
                     readOnly={readOnly} />
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  )
}
